// Browser-side database client - a drop-in replacement for the parts of 'firebase/firestore'
// this app uses (doc/collection/query/where/orderBy/limit, getDoc/getDocs/setDoc/addDoc/
// updateDoc/deleteDoc, writeBatch, runTransaction, onSnapshot, increment/arrayUnion/
// arrayRemove/serverTimestamp, Timestamp). Same names, same call shapes - only the import
// path changed - but every call goes to /api/db, which stores everything in Cloudflare D1.
//
// Login is still Firebase Auth: each request sends the user's Firebase ID token.
//
// Live listeners: D1 has no push channel, so onSnapshot() POLLS. All active listeners are
// batched into ONE request per tick (see the poller at the bottom); the server answers
// "unchanged" for anything that did not change. Your own writes trigger an immediate re-poll,
// so your own actions show up almost instantly. Chat/call listeners poll faster than the rest.
import { firebaseAuth } from './firebase'
import { hashKey, isNarrowDocPath, isNarrowKey, queryKey } from './scope'

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
export type DbErrorCode = 'permission-denied' | 'unauthenticated' | 'not-found' | 'aborted' | 'unavailable' | 'invalid-argument' | 'internal'
export class FirestoreError extends Error {
  code: DbErrorCode
  constructor(code: DbErrorCode, message: string) {
    super(message)
    this.code = code
    this.name = 'FirebaseError'
  }
}

// ---------------------------------------------------------------------------
// Timestamp + FieldValue
// ---------------------------------------------------------------------------
export class Timestamp {
  constructor(public seconds: number, public nanoseconds: number) {}
  static now() { return Timestamp.fromMillis(Date.now()) }
  static fromMillis(ms: number) { return new Timestamp(Math.floor(ms / 1000), (ms % 1000) * 1e6) }
  static fromDate(d: Date) { return Timestamp.fromMillis(d.getTime()) }
  toMillis() { return this.seconds * 1000 + Math.floor(this.nanoseconds / 1e6) }
  toDate() { return new Date(this.toMillis()) }
  isEqual(o: Timestamp) { return !!o && o.toMillis() === this.toMillis() }
  valueOf() { return String(this.toMillis()) }
}

export type FieldValue = { __fv: string; [k: string]: any }
export const serverTimestamp = (): FieldValue => ({ __fv: 'ts' })
export const increment = (n: number): FieldValue => ({ __fv: 'inc', n })
export const arrayUnion = (...v: unknown[]): FieldValue => ({ __fv: 'union', v: v.map(encode) })
export const arrayRemove = (...v: unknown[]): FieldValue => ({ __fv: 'remove', v: v.map(encode) })
export const deleteField = (): FieldValue => ({ __fv: 'del' })

/** JS value -> wire JSON (Date/Timestamp become {__ts}); undefined fields are dropped. */
function encode(v: any): any {
  if (v === undefined) return undefined
  if (v === null || typeof v !== 'object') return v
  if (v instanceof Timestamp) return { __ts: v.toMillis() }
  if (v instanceof Date) return { __ts: v.getTime() }
  if (Array.isArray(v)) return v.map((x) => encode(x) ?? null)
  const out: Record<string, any> = {}
  for (const [k, val] of Object.entries(v)) {
    const e = encode(val)
    if (e !== undefined) out[k] = e
  }
  return out
}

/** wire JSON -> JS value ({__ts} becomes a Timestamp). */
function decode(v: any): any {
  if (v === null || typeof v !== 'object') return v
  if (Array.isArray(v)) return v.map(decode)
  if (typeof v.__ts === 'number') return Timestamp.fromMillis(v.__ts)
  const out: Record<string, any> = {}
  for (const [k, val] of Object.entries(v)) out[k] = decode(val)
  return out
}

// ---------------------------------------------------------------------------
// References
// ---------------------------------------------------------------------------
export type Firestore = { readonly type: 'firestore' }
/** Passed as the first argument to doc()/collection() exactly like the old Firestore instance. */
export const firestore: Firestore = { type: 'firestore' }

const AUTO_ID = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
function autoId(): string {
  const bytes = new Uint8Array(20)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes)
  else for (let i = 0; i < 20; i++) bytes[i] = Math.floor(Math.random() * 256)
  return Array.from(bytes, (b) => AUTO_ID[b % AUTO_ID.length]).join('')
}

export class DocumentReference {
  readonly type = 'document' as const
  constructor(public readonly firestore: Firestore, public readonly path: string) {}
  get id() { return this.path.slice(this.path.lastIndexOf('/') + 1) }
  get parent() { return new CollectionReference(this.firestore, this.path.slice(0, this.path.lastIndexOf('/'))) }
}

export type QuerySpec = {
  parent: string
  filters: { f: string; op: string; v: any }[]
  orders: { f: string; dir: 'asc' | 'desc' }[]
  limit?: number
}

export class Query {
  readonly type: 'query' | 'collection' = 'query'
  constructor(public readonly firestore: Firestore, public readonly spec: QuerySpec) {}
}

export class CollectionReference extends Query {
  readonly type = 'collection' as const
  readonly path: string
  constructor(firestore: Firestore, path: string) {
    super(firestore, { parent: path, filters: [], orders: [] })
    this.path = path
  }
  get id() { return this.path.slice(this.path.lastIndexOf('/') + 1) }
}

const joinPath = (segs: string[]) => segs.flatMap((s) => String(s).split('/')).filter(Boolean).join('/')

export function collection(base: Firestore | DocumentReference | CollectionReference, ...segs: string[]): CollectionReference {
  const prefix = base && (base as any).type === 'firestore' ? '' : (base as any).path
  const path = joinPath([prefix, ...segs].filter(Boolean))
  if (path.split('/').length % 2 !== 1) throw new FirestoreError('invalid-argument', `Invalid collection path: ${path}`)
  return new CollectionReference(firestore, path)
}

export function doc(base: Firestore | CollectionReference | DocumentReference, ...segs: string[]): DocumentReference {
  if (base instanceof CollectionReference) return new DocumentReference(firestore, `${base.path}/${segs[0] || autoId()}`)
  const prefix = (base as any).type === 'firestore' ? '' : (base as any).path
  const path = joinPath([prefix, ...segs].filter(Boolean))
  if (path.split('/').length % 2 !== 0) throw new FirestoreError('invalid-argument', `Invalid document path: ${path}`)
  return new DocumentReference(firestore, path)
}

// Query constraints
type Constraint =
  | { kind: 'where'; f: string; op: string; v: any }
  | { kind: 'orderBy'; f: string; dir: 'asc' | 'desc' }
  | { kind: 'limit'; n: number }
export const where = (f: string, op: string, v: unknown): Constraint => ({ kind: 'where', f, op, v: encode(v) })
export const orderBy = (f: string, dir: 'asc' | 'desc' = 'asc'): Constraint => ({ kind: 'orderBy', f, dir })
export const limit = (n: number): Constraint => ({ kind: 'limit', n })

export function query(q: Query, ...constraints: Constraint[]): Query {
  const spec: QuerySpec = { ...q.spec, filters: [...q.spec.filters], orders: [...q.spec.orders] }
  for (const c of constraints) {
    if (c.kind === 'where') spec.filters.push({ f: c.f, op: c.op, v: c.v })
    else if (c.kind === 'orderBy') spec.orders.push({ f: c.f, dir: c.dir })
    else spec.limit = c.n
  }
  return new Query(q.firestore, spec)
}

// ---------------------------------------------------------------------------
// Snapshots
// ---------------------------------------------------------------------------
const metadata = () => ({ fromCache: false, hasPendingWrites: false })

export class DocumentSnapshot {
  readonly metadata = metadata()
  constructor(public readonly ref: DocumentReference, protected readonly _data: Record<string, any> | null) {}
  get id() { return this.ref.id }
  exists(): boolean { return this._data !== null }
  data(): any { return this._data ?? undefined }
  get(field: string): any {
    return field.split('.').reduce((o: any, k) => (o == null ? undefined : o[k]), this._data)
  }
}
export class QueryDocumentSnapshot extends DocumentSnapshot {
  data(): any { return this._data }
}

export type DocumentChange = { type: 'added' | 'modified' | 'removed'; doc: QueryDocumentSnapshot; oldIndex: number; newIndex: number }
export class QuerySnapshot {
  readonly metadata = metadata()
  readonly size: number
  readonly empty: boolean
  constructor(public readonly docs: QueryDocumentSnapshot[], private readonly _changes: DocumentChange[]) {
    this.size = docs.length
    this.empty = docs.length === 0
  }
  forEach(cb: (d: QueryDocumentSnapshot) => void) { this.docs.forEach(cb) }
  docChanges(): DocumentChange[] { return this._changes }
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------
async function idToken(): Promise<string> {
  const u = (firebaseAuth as any)?.currentUser
  if (!u) throw new FirestoreError('unauthenticated', 'Not signed in')
  // A token refresh on a bad network can hang for minutes and, because every poll / read waits for it, froze the whole
  // app (profiles, chats, games) until a reload. Give up after 10s so the caller fails fast and simply retries.
  let to: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      u.getIdToken() as Promise<string>,
      new Promise<string>((_, rej) => { to = setTimeout(() => rej(new FirestoreError('unavailable', 'Network is slow - try again')), 10000) }),
    ])
  } finally { if (to) clearTimeout(to) }
}

/** Signed-out visitors browse as guests: read ops go to /api/db WITHOUT a token (server returns public data only).
 *  Anything that writes or listens live still needs an account and fails with 'unauthenticated'. */
const GUEST_OPS = new Set(['get', 'query', 'count', 'whoami'])

// How far this device's clock is ahead (+) / behind (-) the server, learned from the HTTP Date header of every call.
// Presence timestamps come from the server clock, so "online" must be judged against server-corrected time.
let serverClockSkewMs = 0
let skewReady = false
let skewBestRtt = Infinity
export const getServerClockSkewMs = () => serverClockSkewMs
// The HTTP Date header only has 1-second resolution and arrives after the network round trip. Using it raw made the
// skew jump by up to a second (or more on slow networks) on EVERY call, so game clocks ran fast / jumped back and forth.
// Now: take the midpoint of the request, centre the truncated second, ignore very slow calls, trust the fastest calls
// most and smooth the rest, so the estimate settles and stops moving.
function learnServerSkew(sentAt: number, recvAt: number, serverDate: number) {
  const rtt = recvAt - sentAt
  if (!Number.isFinite(serverDate) || rtt < 0 || rtt > 4000) return
  const sample = (sentAt + recvAt) / 2 - (serverDate + 500)
  if (!skewReady) { serverClockSkewMs = sample; skewReady = true; skewBestRtt = rtt; return }
  if (rtt <= skewBestRtt) { skewBestRtt = rtt; serverClockSkewMs = serverClockSkewMs * 0.4 + sample * 0.6; return }
  // slower call: only a small nudge, and only when the sample is plausible (within 1.5s of what we already believe)
  if (Math.abs(sample - serverClockSkewMs) < 1500) serverClockSkewMs += (sample - serverClockSkewMs) * 0.1
  skewBestRtt = Math.min(skewBestRtt + 5, 4000) // slowly forget the old best so the estimate can still follow real drift
}

const READ_TIMEOUT_MS = 7000 // one-shot reads (profile, posts, lists): a healthy read answers in ~1-2s, so a stalled one is dropped after 7s
const READ_RETRY_TIMEOUT_MS = 14000 // ...and retried once with more patience

async function callOnce(body: unknown, timeoutMs?: number): Promise<any> {
  let token = ''
  try { token = await idToken() } catch (e) {
    if (!GUEST_OPS.has((body as any)?.op)) throw e
  }
  const sentAt = Date.now()
  // A request that never answers (bad mobile network, half-open connection) used to hang for minutes and, because only
  // one poll could be in flight, froze EVERY live listener (chat, comments, calls, games) with it. The timer now stays
  // armed until the WHOLE answer (headers AND body) has arrived - a stalled body used to hang forever because the timer
  // was cleared as soon as the headers came in.
  const ctrl = timeoutMs && typeof AbortController !== 'undefined' ? new AbortController() : null
  const killer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null
  try {
    let res: Response
    try {
      res = await fetch('/api/db', {
        method: 'POST',
        headers: token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        ...(ctrl ? { signal: ctrl.signal } : {}),
      })
    } catch {
      throw new FirestoreError('unavailable', 'Network error - check your connection')
    }
    const serverDate = Date.parse(res.headers.get('date') || '')
    learnServerSkew(sentAt, Date.now(), serverDate)
    let json: any = null
    try { json = await res.json() } catch (e: any) {
      if (e?.name === 'AbortError' || ctrl?.signal.aborted) throw new FirestoreError('unavailable', 'Network error - check your connection')
      json = null
    }
    if (!res.ok) {
      const code: DbErrorCode = json?.code || (res.status === 401 ? 'unauthenticated' : res.status === 403 ? 'permission-denied' : res.status === 404 ? 'not-found' : res.status === 409 ? 'aborted' : 'internal')
      throw new FirestoreError(code, json?.error || `db-http-${res.status}`)
    }
    return json
  } finally {
    if (killer) clearTimeout(killer)
  }
}

async function call(body: unknown, opts?: { timeoutMs?: number }): Promise<any> {
  const op = (body as any)?.op
  const isRead = GUEST_OPS.has(op) && op !== 'whoami'
  // Reads are safe to repeat, so a dropped / timed-out read is retried once automatically (the usual cause is a mobile
  // network that just switched or woke up). Writes are never repeated here - they could be applied twice.
  const timeoutMs = opts?.timeoutMs ?? (isRead ? READ_TIMEOUT_MS : undefined)
  try {
    return await callOnce(body, timeoutMs)
  } catch (e: any) {
    if (!isRead || e?.code !== 'unavailable') throw e
    await new Promise((r) => setTimeout(r, 400))
    return callOnce(body, opts?.timeoutMs ?? READ_RETRY_TIMEOUT_MS)
  }
}

const wireQuery = (q: Query) => ({ parent: q.spec.parent, filters: q.spec.filters, orders: q.spec.orders, limit: q.spec.limit })

// ---------------------------------------------------------------------------
// Admin check - the browser never knows the admin's email (it lives only in the
// ADMIN_EMAIL secret on the Worker, see lib/d1-rules.ts). This just asks the server
// "is the signed-in account the admin?" and gets back true/false.
// ---------------------------------------------------------------------------
export async function checkIsAdmin(): Promise<boolean> {
  try {
    const r = await call({ op: 'whoami' })
    return r?.isAdmin === true
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// One-shot reads
// ---------------------------------------------------------------------------
// Identical reads that are in flight at the same moment (e.g. the same user's avatar asked by 20 chat rows, or a profile
// opening twice) share ONE request. Cleared on every write so a read after a write never gets the older answer.
const inflightReads = new Map<string, Promise<any>>()
function sharedRead(key: string, run: () => Promise<any>): Promise<any> {
  const hit = inflightReads.get(key)
  if (hit) return hit
  const pr = run().finally(() => { if (inflightReads.get(key) === pr) inflightReads.delete(key) })
  inflightReads.set(key, pr)
  return pr
}

export async function getDoc(ref: DocumentReference): Promise<DocumentSnapshot> {
  const r = await sharedRead(`g:${ref.path}`, () => call({ op: 'get', path: ref.path }))
  return new DocumentSnapshot(ref, r.exists ? decode(r.data) : null)
}

type SnapItem = { id: string; data: any; json?: string }

function buildQuerySnapshot(items: SnapItem[], parent: string, prev?: Map<string, string>): QuerySnapshot {
  const snaps = items.map((d) => {
    const s = new QueryDocumentSnapshot(new DocumentReference(firestore, `${parent}/${d.id}`), d.data)
    // PERF: the poller already holds each row's JSON; keep it so screens can tell "this doc did not change" without re-serialising.
    if (d.json !== undefined) (s as any)._json = d.json
    return s
  })
  const changes: DocumentChange[] = []
  const seen = new Set<string>()
  snaps.forEach((s, i) => {
    seen.add(s.id)
    const old = prev?.get(s.id)
    if (old === undefined) changes.push({ type: 'added', doc: s, oldIndex: -1, newIndex: i })
    else if (old !== (items[i].json ?? JSON.stringify(s.data()))) changes.push({ type: 'modified', doc: s, oldIndex: i, newIndex: i })
  })
  if (prev) {
    for (const id of prev.keys()) {
      if (!seen.has(id)) changes.push({ type: 'removed', doc: new QueryDocumentSnapshot(new DocumentReference(firestore, `${parent}/${id}`), {}), oldIndex: 0, newIndex: -1 })
    }
  }
  return new QuerySnapshot(snaps, changes)
}

export async function getDocs(q: Query): Promise<QuerySnapshot> {
  const wq = wireQuery(q)
  const r = await sharedRead(`q:${JSON.stringify(wq)}`, () => call({ op: 'query', q: wq }))
  return buildQuerySnapshot(r.docs.map((x: any) => ({ id: x.id, data: decode(x.data) })), q.spec.parent)
}

export async function getCountFromServer(q: Query): Promise<{ data: () => { count: number } }> {
  const r = await call({ op: 'count', q: wireQuery(q) })
  return { data: () => ({ count: Number(r.count) || 0 }) }
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
type WireWrite = { op: 'set' | 'update' | 'delete'; path: string; data?: any; merge?: boolean }

async function commit(writes: WireWrite[], guards: { path: string; ver: number }[] = []) {
  inflightReads.clear() // a read started after this write must not share an answer that was fetched before it
  await call({ op: 'commit', writes, guards })
  inflightReads.clear()
  // Show the result of your own write immediately instead of on the next tick - but only wake the listeners that
  // actually watch what was written (before: ALL of them, which on a busy screen meant a dozen needless queries per tap).
  pokeListeners(writes.map((w) => w.path))
}

const encodeData = (data: any) => encode(data) ?? {}

export async function setDoc(ref: DocumentReference, data: any, options?: { merge?: boolean }): Promise<void> {
  await commit([{ op: 'set', path: ref.path, data: encodeData(data), merge: !!options?.merge }])
}
export async function updateDoc(ref: DocumentReference, data: any): Promise<void> {
  await commit([{ op: 'update', path: ref.path, data: encodeData(data) }])
}
export async function deleteDoc(ref: DocumentReference): Promise<void> {
  await commit([{ op: 'delete', path: ref.path }])
}
export async function addDoc(coll: CollectionReference, data: any): Promise<DocumentReference> {
  const ref = doc(coll)
  await commit([{ op: 'set', path: ref.path, data: encodeData(data) }])
  return ref
}

export class WriteBatch {
  private writes: WireWrite[] = []
  set(ref: DocumentReference, data: any, options?: { merge?: boolean }) { this.writes.push({ op: 'set', path: ref.path, data: encodeData(data), merge: !!options?.merge }); return this }
  update(ref: DocumentReference, data: any) { this.writes.push({ op: 'update', path: ref.path, data: encodeData(data) }); return this }
  delete(ref: DocumentReference) { this.writes.push({ op: 'delete', path: ref.path }); return this }
  async commit(): Promise<void> {
    if (this.writes.length) await commit(this.writes)
    this.writes = []
  }
}
export const writeBatch = (_db?: Firestore) => new WriteBatch()

export class Transaction {
  writes: WireWrite[] = []
  guards = new Map<string, number>()
  async get(ref: DocumentReference): Promise<DocumentSnapshot> {
    const r = await call({ op: 'get', path: ref.path })
    this.guards.set(ref.path, r.ver ?? 0) // commit fails (and the callback re-runs) if this doc changed meanwhile
    return new DocumentSnapshot(ref, r.exists ? decode(r.data) : null)
  }
  set(ref: DocumentReference, data: any, options?: { merge?: boolean }) { this.writes.push({ op: 'set', path: ref.path, data: encodeData(data), merge: !!options?.merge }); return this }
  update(ref: DocumentReference, data: any) { this.writes.push({ op: 'update', path: ref.path, data: encodeData(data) }); return this }
  delete(ref: DocumentReference) { this.writes.push({ op: 'delete', path: ref.path }); return this }
}

export async function runTransaction<T>(_db: Firestore, fn: (tx: Transaction) => Promise<T>): Promise<T> {
  let lastErr: unknown
  for (let attempt = 0; attempt < 8; attempt++) {
    if (attempt) await new Promise((r) => setTimeout(r, Math.random() * 25 * attempt))
    const tx = new Transaction()
    try {
      const result = await fn(tx)
      if (tx.writes.length) await commit(tx.writes, [...tx.guards].map(([path, ver]) => ({ path, ver })))
      return result
    } catch (e: any) {
      lastErr = e
      if (e?.code === 'aborted') continue // another write got in between - run the callback again
      throw e
    }
  }
  throw lastErr
}

// ---------------------------------------------------------------------------
// Live listeners (polling + real-time doorbell)
// ---------------------------------------------------------------------------
// Three things make this fast:
//  1. DOORBELL: one WebSocket to the Hub Durable Object (custom-worker.ts). When anybody writes something this phone
//     listens to, the hub rings it and the matching listener polls IMMEDIATELY (~100 ms) instead of waiting for its timer.
//     No data goes through the socket - the poll still reads via /api/db, so all security rules stay in force. If the
//     socket is down (or the hub is not deployed) everything silently falls back to the old timer-based polling.
//  2. LANES: calls/chat/comments, friends/notifications and "everything else" are polled by three independent requests,
//     so a heavy feed query can never delay a chat message or an incoming call.
//  3. DELTAS: a changed query sends only the documents that changed (+ the ordered id list), not the whole result again.
type Row = { ver: number; wire: any; json: string }

type Listener = {
  id: number
  kind: 'doc' | 'query'
  ref?: DocumentReference
  query?: Query
  path: string // doc path, or the collection a query reads
  hash: number // doorbell channel (hash of the doc path / scoped query key)
  push: boolean // does this listener get doorbells (narrow channel) - see lib/scope.ts
  lane: 0 | 1 | 2
  ver: number // -1 => never fetched, forces the first snapshot
  since: number // newest document version held locally (for delta responses)
  rows?: Map<string, Row>
  intervalMs: number
  nextAt: number
  lastPollAt: number
  polling: boolean
  dirty: boolean // poked while its poll was in flight -> poll again right after
  prev?: Map<string, string>
  next: (snap: any) => void
  error?: (e: FirestoreError) => void
}

const listeners = new Map<number, Listener>()
let nextListenerId = 1
let timer: ReturnType<typeof setInterval> | null = null
const laneBusy = [false, false, false]
const POLL_BATCH = 50

function intervalFor(path: string): number {
  if (/^(calls|groupCalls)(\/|$)/.test(path)) return 500 // WebRTC signalling: latency matters most
  if (/\/messages$/.test(path) || /^typing\//.test(path)) return 350 // open chat: keep it snappy
  if (/\/comments$/.test(path)) return 700 // open comments sheet: new comments/likes should feel snappy too
  if (/^ludoRooms(\/|$)/.test(path)) return 450 // live Ludo table (room state + in-game chat/emoji)
  if (/^chessRooms(\/|$)/.test(path)) return 600 // live Chess table (moves + clocks)
  if (/^carromRooms\/[^/]+\/aim$/.test(path)) return 250 // live aim of the player whose turn it is
  if (/^carromRooms(\/|$)/.test(path)) return 450 // live Carrom table (board + shots + chat)
  if (/^presence/.test(path)) return 5000 // low priority, doesn't need to be instant
  if (/^friendRequests$/.test(path) || /^friends\/[^/]+\/list$/.test(path)) return 900 // connect / accept should show up on the other phone within ~1s
  if (/^notifications\/[^/]+\/items$/.test(path) || /^dmRequests$/.test(path) || /^groupChats$/.test(path)) return 1200
  return 1500 // everything else (posts, stories, group list...)
}
const laneFor = (ms: number): 0 | 1 | 2 => (ms <= 800 ? 0 : ms <= 1200 ? 1 : 2)
/** Smallest gap between two polls of the same listener when the doorbell rings (keeps a chatty group from hammering the server). */
const RING_GAP_MS = [30, 200, 500]
/** While the doorbell socket is healthy, a listener that gets doorbells only needs a slow safety-net poll. */
const SAFETY_POLL_MS = [900, 3000, 5000]

const parentOfPath = (p: string) => p.slice(0, p.lastIndexOf('/'))
const isHidden = () => typeof document !== 'undefined' && document.hidden

function effectiveInterval(l: Listener): number {
  let ms = l.intervalMs
  if (wsOpen && l.push) ms = Math.max(ms, SAFETY_POLL_MS[l.lane])
  if (isHidden()) ms = Math.max(ms, 12000)
  return ms
}

let tickTimeout: ReturnType<typeof setTimeout> | null = null
function scheduleTick(ms: number) {
  if (typeof window === 'undefined') return
  if (tickTimeout) return
  tickTimeout = setTimeout(() => { tickTimeout = null; tick() }, Math.max(0, ms))
}

function kick() {
  if (typeof window === 'undefined') return
  if (!timer) timer = setInterval(() => tick(), 150)
  installWindowHooks()
  syncSubscriptions()
  void wsConnect()
  scheduleTick(0)
}

function wake(l: Listener, gap: number) {
  if (l.polling) { l.dirty = true; return }
  l.nextAt = Math.min(l.nextAt, Math.max(Date.now(), l.lastPollAt + gap))
}

/** Ask listeners to check for changes right now. With `paths` (what you just wrote) only the listeners that watch those
 *  documents / their collections are woken; without, all of them (used by the Ludo/Chess server actions). */
export function pokeListeners(paths?: string[]) {
  if (!listeners.size) return
  const docs = paths ? new Set(paths) : null
  const parents = paths ? new Set(paths.map(parentOfPath)) : null
  listeners.forEach((l) => {
    if (docs && parents && !(l.kind === 'doc' ? docs.has(l.path) : parents.has(l.path))) return
    wake(l, 0)
  })
  scheduleTick(10)
}

/** Wake only the listeners whose path starts with one of `prefixes` (e.g. a Ludo action only needs the Ludo room
 *  listeners, not the feed, stories and chat lists - waking all of them made every dice roll fire a dozen heavy queries). */
export function pokeListenersByPrefix(prefixes: string[]) {
  if (!listeners.size) return
  listeners.forEach((l) => { if (prefixes.some((p) => l.path.startsWith(p))) wake(l, 0) })
  scheduleTick(10)
}

function tick() {
  if (!listeners.size) {
    if (timer) { clearInterval(timer); timer = null }
    wsClose()
    return
  }
  for (const lane of [0, 1, 2] as const) if (!laneBusy[lane]) void runLane(lane)
}

async function runLane(lane: 0 | 1 | 2) {
  const startedAt = Date.now()
  // The server answers at most 60 listeners per request and silently ignores the rest - those listeners then never
  // updated. Send at most POLL_BATCH, the longest-waiting first; the remainder goes out on the very next tick.
  const due = [...listeners.values()].filter((l) => l.lane === lane && l.nextAt <= startedAt).sort((a, b) => a.nextAt - b.nextAt).slice(0, POLL_BATCH)
  if (!due.length) return
  laneBusy[lane] = true
  due.forEach((l) => { l.polling = true; l.dirty = false; l.lastPollAt = startedAt })
  let failed = false
  try {
    const payload = due.map((l) => l.kind === 'doc'
      ? { id: l.id, kind: 'doc', path: l.ref!.path, ver: l.ver }
      : { id: l.id, kind: 'query', q: wireQuery(l.query!), ver: l.ver, since: l.since, delta: true })
    const res = await call({ op: 'poll', listeners: payload }, { timeoutMs: lane === 0 ? 6000 : 9000 }) // lane 0 = calls / chat / games: a stuck request must not hold them for long
    for (const r of res.results || []) {
      const l = listeners.get(r.id)
      if (!l) continue
      if (r.error) {
        listeners.delete(l.id)
        syncSubscriptions()
        try { l.error?.(new FirestoreError((r.code as DbErrorCode) || 'internal', r.error)) } catch { /* ignore */ }
        continue
      }
      if (!r.changed) continue
      try {
        if (l.kind === 'doc') {
          l.ver = r.ver
          l.next(new DocumentSnapshot(l.ref!, r.exists ? decode(r.data) : null))
        } else {
          const items = applyQueryResult(l, r)
          if (!items) { l.ver = -1; l.since = 0; l.rows = undefined; l.dirty = true; continue } // local copy out of sync: re-download everything
          l.ver = r.ver
          const snap = buildQuerySnapshot(items, l.query!.spec.parent, l.prev)
          l.prev = new Map(items.map((d) => [d.id, d.json as string]))
          l.next(snap)
        }
      } catch (e) {
        console.error('onSnapshot callback threw', e)
      }
    }
  } catch (e: any) {
    // Offline / signed out / timeout / server hiccup: keep every listener and simply retry on the next tick.
    failed = true
    if (e?.code === 'permission-denied') console.warn('listener poll denied', e)
  } finally {
    const done = Date.now()
    due.forEach((l) => {
      l.polling = false
      l.nextAt = l.dirty ? done : done + (failed ? Math.max(effectiveInterval(l), 1500) : effectiveInterval(l))
      l.dirty = false
    })
    laneBusy[lane] = false
    if (due.some((l) => l.nextAt <= done)) scheduleTick(0)
  }
}

/** Merge a poll answer into the listener's local copy. Returns the full ordered result, or null if the copy is out of sync. */
function applyQueryResult(l: Listener, r: any): SnapItem[] | null {
  const old = l.rows
  const fresh = new Map<string, Row>()
  const incoming = new Map<string, { id: string; data: any; ver: number }>((r.docs || []).map((x: any) => [x.id, x] as [string, any]))
  const rowFor = (id: string, ver: number): Row | null => {
    const cached = old?.get(id)
    if (cached && cached.ver === ver) return cached
    const x = incoming.get(id)
    if (!x || x.ver !== ver) return null
    return { ver, wire: x.data, json: JSON.stringify(decode(x.data)) }
  }
  const order: [string, number][] = Array.isArray(r.order) ? r.order : (r.docs || []).map((x: any) => [x.id, x.ver])
  for (const [id, ver] of order) {
    const row = rowFor(id, ver)
    if (!row) return null // a document we do not hold and the server did not send
    fresh.set(id, row)
  }
  l.rows = fresh
  let since = 0
  fresh.forEach((row) => { if (row.ver > since) since = row.ver })
  l.since = since
  return order.map(([id]) => ({ id, data: decode(fresh.get(id)!.wire), json: fresh.get(id)!.json }))
}

// ---------------------------------------------------------------------------
// Doorbell socket
// ---------------------------------------------------------------------------
let ws: WebSocket | null = null
let wsOpen = false
let wsFails = 0
let wsRetryTimer: ReturnType<typeof setTimeout> | null = null
let wsPingTimer: ReturnType<typeof setInterval> | null = null
let wsPongTimer: ReturnType<typeof setTimeout> | null = null
let subTimer: ReturnType<typeof setTimeout> | null = null
let hooksInstalled = false

const wantsDoorbell = () => { for (const l of listeners.values()) if (l.push) return true; return false }

function wsClose() {
  if (wsPingTimer) { clearInterval(wsPingTimer); wsPingTimer = null }
  if (wsPongTimer) { clearTimeout(wsPongTimer); wsPongTimer = null }
  if (wsRetryTimer) { clearTimeout(wsRetryTimer); wsRetryTimer = null }
  const s = ws
  ws = null
  wsOpen = false
  if (s) { s.onopen = s.onmessage = s.onclose = s.onerror = null; try { s.close() } catch { /* ignore */ } }
}

function wsLost() {
  const wasOpen = wsOpen
  wsClose()
  if (!listeners.size) return
  wsFails = wasOpen ? 0 : wsFails + 1
  // Back-off 1s, 2s, 4s ... 60s. A missing hub (404 / not deployed) therefore costs almost nothing.
  const delay = Math.min(60000, 1000 * 2 ** Math.min(wsFails, 6))
  wsRetryTimer = setTimeout(() => { wsRetryTimer = null; void wsConnect() }, delay)
  // Doorbell gone: bring every listener back to its normal polling speed right away.
  listeners.forEach((l) => { l.nextAt = Math.min(l.nextAt, Date.now() + l.intervalMs) })
}

async function wsConnect() {
  if (ws || wsRetryTimer || typeof window === 'undefined' || typeof WebSocket === 'undefined' || !wantsDoorbell()) return
  let token: string
  try { token = await idToken() } catch { return }
  if (ws || !wantsDoorbell()) return
  let sock: WebSocket
  try {
    sock = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/rt`, ['animora-rt', token])
  } catch { wsLost(); return }
  ws = sock
  sock.onopen = () => {
    if (ws !== sock) return
    wsOpen = true
    wsFails = 0
    syncSubscriptions(true)
    // Anything that changed while the socket was down: look once, right now.
    listeners.forEach((l) => { if (l.push) wake(l, 0) })
    scheduleTick(0)
    wsPingTimer = setInterval(() => {
      if (ws !== sock || sock.readyState !== 1) return
      try { sock.send('ping') } catch { return }
      if (wsPongTimer) clearTimeout(wsPongTimer)
      wsPongTimer = setTimeout(() => { if (ws === sock) wsLost() }, 8000) // no answer = half-dead connection
    }, 20000)
  }
  sock.onmessage = (ev) => {
    if (ws !== sock) return
    if (ev.data === 'pong') { if (wsPongTimer) { clearTimeout(wsPongTimer); wsPongTimer = null } return }
    let msg: any
    try { msg = JSON.parse(String(ev.data)) } catch { return }
    if (msg?.t !== 'c' || !Array.isArray(msg.h)) return
    const rung = new Set<number>(msg.h)
    let any = false
    listeners.forEach((l) => { if (l.push && rung.has(l.hash)) { wake(l, RING_GAP_MS[l.lane]); any = true } })
    if (any) scheduleTick(0)
  }
  sock.onclose = () => { if (ws === sock) wsLost() }
  sock.onerror = () => { if (ws === sock) wsLost() }
}

/** Tell the hub which channels this phone listens to (whole set, debounced). */
function syncSubscriptions(now = false) {
  if (subTimer) clearTimeout(subTimer)
  const send = () => {
    subTimer = null
    if (!ws || !wsOpen || ws.readyState !== 1) return
    const hashes = new Set<number>()
    listeners.forEach((l) => { if (l.push) hashes.add(l.hash) })
    try { ws.send(JSON.stringify({ t: 's', h: [...hashes].slice(0, 150) })) } catch { /* the close handler reconnects */ }
  }
  if (now) send()
  else subTimer = setTimeout(send, 40)
}

function installWindowHooks() {
  if (hooksInstalled || typeof window === 'undefined') return
  hooksInstalled = true
  const refresh = () => {
    if (!listeners.size) return
    if (!ws && wsRetryTimer) { clearTimeout(wsRetryTimer); wsRetryTimer = null; wsFails = 0 }
    void wsConnect()
    listeners.forEach((l) => wake(l, 0)) // coming back to the app / network: refresh everything once
    scheduleTick(0)
  }
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh() })
  window.addEventListener('online', refresh)
  window.addEventListener('focus', refresh)
}

type Observer<T> = ((snap: T) => void) | { next?: (snap: T) => void; error?: (e: FirestoreError) => void }

export function onSnapshot(ref: DocumentReference, next: Observer<DocumentSnapshot>, error?: (e: FirestoreError) => void): () => void
export function onSnapshot(q: Query, next: Observer<QuerySnapshot>, error?: (e: FirestoreError) => void): () => void
export function onSnapshot(target: any, nextOrObserver: any, error?: (e: FirestoreError) => void): () => void {
  const next = typeof nextOrObserver === 'function' ? nextOrObserver : nextOrObserver?.next || (() => {})
  const onError = typeof nextOrObserver === 'function' ? error : nextOrObserver?.error || error
  const isDoc = target instanceof DocumentReference
  const id = nextListenerId++
  const path = isDoc ? target.path : target.spec.parent
  let intervalMs = intervalFor(path)
  // PERF: the chat list keeps two tiny BACKGROUND listeners per friend (last-message "seen" tick + unread badge). They are
  // limited / filtered queries on chats/{id}/messages and used to share the fast 700ms chat lane with the OPEN chat, so
  // 20 friends = 40 extra queries in every chat poll and the open 1:1 chat crawled (groups have no such listeners, so they
  // felt fast). They now poll in the slow lane (still instant via the doorbell); only the full open-chat query stays fast.
  if (!isDoc && /\/messages$/.test(path) && (target.spec.limit != null || target.spec.filters.length > 0)) intervalMs = Math.max(intervalMs, 900)
  const key = isDoc ? target.path : queryKey(target.spec)
  listeners.set(id, {
    id,
    kind: isDoc ? 'doc' : 'query',
    ref: isDoc ? target : undefined,
    query: isDoc ? undefined : target,
    path,
    hash: hashKey(key),
    push: isDoc ? isNarrowDocPath(path) : isNarrowKey(key),
    lane: laneFor(intervalMs),
    ver: -1,
    since: 0,
    intervalMs,
    nextAt: 0,
    lastPollAt: 0,
    polling: false,
    dirty: false,
    next,
    error: onError,
  })
  kick()
  return () => { listeners.delete(id); syncSubscriptions() }
}
